import { useEffect, useState } from 'react';
import { Button, Col, Form, GetProp, Input, Row, Switch, Upload, UploadProps } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onGetBank, onUpdateBank } from 'modules/banks/services/banks';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	logo: any;
	active: 0 | 1;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function UpdateBankPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [updateForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	useEffect(() => {
		getBank();
	}, []);

	const getBank = async () => {
		try {
			const response = await onGetBank(id as string);
			const bank = response.data.data;

			updateForm.setFieldsValue({ 'name[ar]': bank.name.ar });
			updateForm.setFieldsValue({ 'name[en]': bank.name.en });
			updateForm.setFieldsValue({ active: bank.active });
			setImageUrl(bank.logo_url);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('BANKS.FETCH_ERROR'));
			navigate('/banks');
		}
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateBankPayload = new FormData();
			updateBankPayload.append('_method', 'put');
			updateBankPayload.append('name[ar]', values['name[ar]']);
			updateBankPayload.append('name[en]', values['name[en]']);
			updateBankPayload.append('active', values.active ? '1' : '0');

			if (values.logo) updateBankPayload.append('logo', values.logo);

			await onUpdateBank(id as string, updateBankPayload);
			NotificationService.error(t('COMMON.ERROR'), t('BANKS.FETCH_ERROR'));
			navigate('/banks');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('BANKS.UPDATE_ERROR'));
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		updateForm.setFieldsValue({ logo: info.file });
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('BANKS.UPDATE_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('BANKS.DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<h6>{t('BANKS.INFORMATION')}</h6>
								<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('BANKS.VALIDATION.NAME_EN') }]}>
									<Input size="large" placeholder={t('BANKS.PLACEHOLDER.NAME_EN')} style={inputStyles} />
								</Form.Item>

								<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('BANKS.VALIDATION.NAME_AR') }]}>
									<Input size="large" placeholder={t('BANKS.PLACEHOLDER.NAME_AR')} style={inputStyles} />
								</Form.Item>

								<div className="d-flex gap-2 mb-3">
									<Form.Item<FieldType> name="active" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>
							</Col>

							<Col span={24} md={12}>
								<div>
									<h6>{t('BANKS.LOGO')}</h6>
									<Form.Item<FieldType> name="logo" hidden>
										<Input />
									</Form.Item>
									<Upload
										className="squareInput"
										listType="picture-card"
										showUploadList={false}
										beforeUpload={beforeUpload}
										onChange={handleChange}>
										{imageUrl ? (
											<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
										) : (
											<button style={{ border: 0, background: 'none' }} type="button">
												{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
												<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
											</button>
										)}
									</Upload>
								</div>
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							{t('COMMON.UPDATE')}
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateBankPage;
